import axios from 'axios';
import store from '@/store';
import router from '@/router';

// Create axios instance with base URL and default headers
const apiClient = axios.create({
  //baseURL: 'http://localhost:5000/', // Adjust this to match your .NET API URL 
  //baseURL: '/api',
  baseURL: process.env.VUE_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true  // CRITICAL - must be true for cookies
});

// Request interceptor for API requests
apiClient.interceptors.request.use(
  config => {
    // With HttpOnly cookies, the browser will automatically send the cookie
    // We don't need to manually add the Authorization header anymore
    // The JWT cookie is automatically included in requests to the same domain
    
    // We can add CSRF protection here if needed in the future

    // Try cookie first (for same-domain in dev)
    // Fallback to localStorage token

    // Remove any localStorage token logic
    // Cookies are sent automatically with withCredentials: true
    // const token = localStorage.getItem('jwt');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Check if the error is a 401 Unauthorized response
    const is401 = error.response && error.response.status === 401;

    if (is401) {
      //console.log('Auth error detected: 401 Unauthorized', error);

      // Static flag to prevent multiple redirects for simultaneous 401s
      if (!apiClient.isHandlingAuthError) {
        apiClient.isHandlingAuthError = true;
        console.log('Auth error detected: 401 Unauthorized');


        // Avoid logout/redirect if already on login page to prevent loops
      if (router.currentRoute.value.path !== '/login') {
        console.log('Dispatching logout and redirecting to login.');
        // Dispatch logout action to clear auth state
        store.dispatch('logout'); 

        // Use window.location for a hard redirect instead of Vue router
        // This creates a cleaner transition and cancels pending requests
        const redirectUrl = `/login?redirect=${encodeURIComponent(router.currentRoute.value.fullPath)}&expired=true`;
        window.location.href = redirectUrl;
          
        // Return a promise that never resolves to swallow the error and prevent error propagation
        // since we've handled it with a redirect.
        return new Promise(() => {});

      } 
      else {
       // We're already handling a 401 error, swallow this one
       console.log('Additional 401 error caught while redirect is in progress');
       return new Promise(() => {});
      }




      }

      
    } 
    
    // For all other errors (non-401), reject the promise so they can be handled elsewhere if needed.
    console.error('Unhandled API error:', error); // Log other errors
    return Promise.reject(error);
  }
);

export default {
  // Auth endpoints
  async login(credentials) {

    const response = await apiClient.post('/users/login', credentials);
  
    // // Store token if cookie fails
    // if (response.data.token) {
    //   localStorage.setItem('jwt', response.data.token);
    //   localStorage.setItem('jwtExpiry', response.data.expiry);
    // }
    
    return response;

    //return apiClient.post('/users/login', credentials, { withCredentials: true });
  },
  
  register(userData) {
    return apiClient.post('/users/register', userData, { withCredentials: true });
  },
  
  verifyEmail(token) {
    return apiClient.post('/users/verify-email', { token }, { withCredentials: true });
  },
  
  resendVerification(email) {
    return apiClient.post('/users/resend-verification', { email }, { withCredentials: true });
  },
  
  logout(userData) {
    //localStorage.removeItem('jwt');
    //localStorage.removeItem('jwtExpiry');
    return apiClient.post('/users/logout', userData);
  },
  
  // User profile
  getProfile() {
    return apiClient.get('/users/profile', { withCredentials: true });
  },
  
  
  // Search functionality
  search(params) {
    console.log('Search params:', params);
    return apiClient.post('/searchresults', params, { withCredentials: true });
  },
  
  // Filter options endpoints with API fallback to mock data
  // Trainers endpoint exists and works fine
  // getTrainers(searchTerm = '') {
  //   return apiClient.get('/filters/trainers', { 
  //     params: searchTerm ? { search: searchTerm } : {},
  //     withCredentials: true 
  //   });
  // },
  
  async getTrainers(searchTerm = '') {
    try {
      return await apiClient.get('/filters/trainers', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch trainers data', err);
      throw err;
    }
  },

  async getJockeys(searchTerm = '') {
    try {
      return await apiClient.get('/filters/jockeys', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch jockeys data', err);
      throw err;
    }
  },

  async getCourses(searchTerm = '') {
    try {
      return await apiClient.get('/filters/courses', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch courses data', err);
      throw err;
    }
  },

  async getRegions(searchTerm = '') {
    try {
      return await apiClient.get('/filters/regions', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch regions data', err);
      throw err;
    }
  },

  async getTypes(searchTerm = '') {
    try {
      return await apiClient.get('/filters/types', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch types data', err);
      throw err;
    }
  },

  async getDistances(searchTerm = '') {
    try {
      return await apiClient.get('/filters/distances', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch distances data', err);
      throw err;
    }
  },

  async getGoings(searchTerm = '') {
    try {
      return await apiClient.get('/filters/goings', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch goings data', err);
      throw err;
    }
  },

  async getSurfaces(searchTerm = '') {
    try {
      return await apiClient.get('/filters/surfaces', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch surfaces data', err);
      throw err;
    }
  },

  async getAges(searchTerm = '') {
    try {
      return await apiClient.get('/filters/ages', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch ages data', err);
      throw err;
    }
  },

  async getSexes(searchTerm = '') {
    try {
      return await apiClient.get('/filters/sexes', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch sexes data', err);
      throw err;
    }
  },

  async getSires(searchTerm = '') {
    try {
      return await apiClient.get('/filters/sires', {
        params: { ...(searchTerm ? { search: searchTerm } : {}), limit: 200 },
        withCredentials: true
      });
    } catch (err) {
      console.error('Failed to fetch sires data', err);
      throw err;
    }
  },

  // Systems endpoint methods
  async saveSystem(systemData) {
    try {
      return await apiClient.post('/systems', systemData, { withCredentials: true });
    } catch (err) {
      console.error('Failed to save system', err);
      throw err;
    }
  },

  async getSystems(userId) {
    try {
      return await apiClient.get('/systems', { 
        params: { userId }, 
        withCredentials: true 
      });
    } catch (err) {
      console.error('Failed to fetch systems', err);
      throw err;
    }
  },

  async getSystemMetrics(systemId) {
    try {
      return await apiClient.get(`/systems/${systemId}/metrics`, { withCredentials: true });
    } catch (err) {
      console.error('Failed to fetch system metrics', err);
      throw err;
    }
  },
  
  // Analytics dashboard endpoint
  async getAnalytics(params) {
    try {
      return await apiClient.post('/analytics', params, { withCredentials: true });
    } catch (err) {
      console.error('Failed to fetch analytics data', err);
      throw err;
    }
  },

  // Generic POST method for future endpoints
  post(endpoint, data) {
    return apiClient.post(endpoint, data, { withCredentials: true });
  }
};
